import React from 'react';
import { motion } from 'framer-motion';
import { Check, Target, Sparkles, Banknote, ClipboardList } from 'lucide-react';
import { Job } from '../../../types/jobs';
import ApplicationForm from '../../../components/ApplicationForm';

interface JobDetailContentProps {
  job: Job;
}

const subsectionHeadings = new Set([
  'HR & People Administration',
  'Finance & Accounts Administration',
  'Business & Office Administration',
  'PA & Executive Support',
  'Basic IT Coordination',
  'Lead or Co-lead Broadcast Workflow & Legacy Integration',
  'Interface with the IPFM Team led by IPFM experts who will:',
  'Shared Responsibilities',
  'SDI Facility Engineering',
  'Live Sports Production Workflows',
  'Timing, Audio & Ancillary Signals',
  'SDI/IP Gateway & ST 2110 Integration',
  'Migration, Commissioning & Acceptance',
  'Relevant Project Experience',
  'Examples:',
]);

const StaggerList = ({ items }: { items: string[] }) => (
  <ul className="space-y-4">
    {items.map((item, index) => {
      const isSubsectionHeading = subsectionHeadings.has(item);

      return (
        <motion.li
          key={index}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: index * 0.1 }}
          className={isSubsectionHeading ? 'pt-4 first:pt-0' : 'flex items-start gap-3'}
        >
          {isSubsectionHeading ? (
            <h3 className="text-lg font-semibold text-gray-900">{item}</h3>
          ) : (
            <>
              <span className="w-2 h-2 rounded-full bg-accent mt-2.5 flex-shrink-0" aria-hidden="true" />
              <span className="text-gray-700">{item}</span>
            </>
          )}
        </motion.li>
      );
    })}
  </ul>
);

const Section = ({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) => (
  <div>
    <h2 className="text-2xl font-display font-semibold text-gray-900 mb-6 flex items-center gap-3">
      <span className="text-accent">{icon}</span>
      {title}
    </h2>
    {children}
  </div>
);

const JobDetailContent = ({ job }: JobDetailContentProps) => {
  return (
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-[65%_35%] gap-12">
          {/* Left Column - Job Details */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-12"
          >
            {/* Reports To */}
            {job.reports_to && (
              <div className="flex items-center gap-3 text-gray-700">
                <span className="font-semibold text-gray-900">Reports to:</span>
                <span>{job.reports_to}</span>
              </div>
            )}

            {/* Purpose / Mission */}
            {job.purpose && (
              <Section icon={<Target className="w-6 h-6" />} title={job.purpose_heading || 'Role Purpose'}>
                <p className="text-lg text-gray-700 leading-relaxed">{job.purpose}</p>
              </Section>
            )}

            {/* Responsibilities */}
            {job.responsibilities.length > 0 && (
              <Section icon={<ClipboardList className="w-6 h-6" />} title="Key Responsibilities">
                <StaggerList items={job.responsibilities} />
              </Section>
            )}

            {/* Measures of Success */}
            {job.measures_of_success && job.measures_of_success.length > 0 && (
              <Section icon={<Target className="w-6 h-6" />} title="Measures of Success">
                <StaggerList items={job.measures_of_success} />
              </Section>
            )}

            {/* Qualifications & Experience */}
            {job.required_experience_skills.length > 0 && (
              <Section icon={<Check className="w-6 h-6" />} title="Qualifications & Experience">
                <StaggerList items={job.required_experience_skills} />
              </Section>
            )}

            {/* Must Have */}
            {job.required_technical_capabilities && job.required_technical_capabilities.length > 0 && (
              <Section icon={<Check className="w-6 h-6" />} title="Must Have">
                <StaggerList items={job.required_technical_capabilities} />
              </Section>
            )}

            {/* Nice to Have */}
            {job.nice_to_have && job.nice_to_have.length > 0 && (
              <Section icon={<Sparkles className="w-6 h-6" />} title="Nice to Have">
                <StaggerList items={job.nice_to_have} />
              </Section>
            )}

            {/* Compensation */}
            {job.compensation && (
              <Section icon={<Banknote className="w-6 h-6" />} title="Compensation">
                <p className="text-gray-700 leading-relaxed">{job.compensation}</p>
              </Section>
            )}

            {/* Working Requirements */}
            {job.working_requirements && (
              <Section icon={<ClipboardList className="w-6 h-6" />} title="Working Requirements">
                <p className="text-gray-700 leading-relaxed">{job.working_requirements}</p>
              </Section>
            )}
          </motion.div>

          {/* Right Column - Application Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="lg:sticky lg:top-24 lg:self-start"
          >
            <ApplicationForm jobId={job.id} jobTitle={job.role} />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default JobDetailContent;
